import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import OrderView from "@/components/OrderView";
import EmberCanvas from "@/components/EmberCanvas";

export function AuthPage({ mode }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isReg = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = isReg ? await register(f.name, f.email, f.password) : await login(f.email, f.password);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/account");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-[80vh] flex items-center overflow-hidden" data-testid={`${mode}-page`}>
      <img src="/brand/hero.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-[#050505]/70" />
      <EmberCanvas count={40} />
      <div className="relative max-w-[1100px] w-full mx-auto px-5 grid md:grid-cols-[1.1fr_1fr] gap-12 items-center py-16">
        <div className="hidden md:block">
          <img src="/brand/logo.webp" alt="" className="w-72 mb-8" />
          <h1 className="font-display text-6xl chrome-text leading-[0.95]">{isReg ? "Enlist" : "Welcome back, pilot"}</h1>
        </div>
        <form onSubmit={submit} className="glass p-8 space-y-4 clip-slash" data-testid={`${mode}-form`}>
          <div className="overline-label">{isReg ? "Create account" : "Sign in"}</div>
          {isReg && <input required className="field" placeholder="Display name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="register-name-input" />}
          <input required type="email" className="field" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid={`${mode}-email-input`} />
          <input required type="password" className="field" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid={`${mode}-password-input`} />
          {err && <div className="text-[#ff2a55] text-sm" data-testid={`${mode}-error`}>{err}</div>}
          <button className="btn-bomb w-full" disabled={busy} data-testid={`${mode}-submit-button`}>{busy ? "..." : isReg ? "Create account" : "Login"}</button>
          <div className="text-xs text-[#999] text-center">
            {isReg ? <>Have an account? <Link className="text-[#ff2a55] underline" to="/login" data-testid="goto-login-link">Login</Link></> : <>New here? <Link className="text-[#ff2a55] underline" to="/register" data-testid="goto-register-link">Create account</Link></>}
          </div>
        </form>
      </div>
    </div>
  );
}

export function Account() {
  const { user } = useAuth();
  const { data: orders = [] } = useQuery({
    queryKey: ["mine"],
    queryFn: () => api.get("/orders/mine").then((r) => r.data),
    enabled: !!user,
    refetchInterval: (query) => query.state.data?.some((order) => order.status === "pending") ? 5000 : false,
  });
  return (
    <div className="max-w-3xl mx-auto px-5 pt-16" data-testid="account-page">
      <div className="overline-label mb-4">// hangar</div>
      <h1 className="font-display text-6xl md:text-7xl leading-[0.9] chrome-text mb-10">{user?.name}'s orders</h1>
      <div className="space-y-8">
        {orders.length === 0 && <div className="text-[#888]" data-testid="account-empty">No orders yet. <Link to="/shop" className="text-[#ff2a55] underline">Go shopping</Link>.</div>}
        {orders.map((o) => <OrderView key={o.id} o={o} />)}
      </div>
    </div>
  );
}
